import { Context, ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { MenuConfig } from '../../api/menu-config';
import { api } from '../../api';
import { v4 as uuid } from 'uuid';
import { EnumIcon } from '../../data/IconMap';
import { PageConfig } from '../../api/page-config';
import { useAuth } from '../auth/AuthProvider';
import { useStorage } from '../../hooks/useStorage';
import { UserGroup } from '../../api/user-group';

export type Global = {
    minify: boolean;
    setMinify: (isLoading: boolean) => void;
    hideText: boolean;
    setHideText: (isLoading: boolean) => void;
    expandedItems: string[];
    setExpandedItems: (expandedItems: string[]) => void;
    userGroups: UserGroup[];
    setUserGroups: (menuConfigs: UserGroup[]) => void;
    reloadUserGroups: () => Promise<false | void>;
    menuConfigs: MenuConfig[];
    setMenuConfigs: (menuConfigs: MenuConfig[]) => void;
    reloadMenuConfigs: () => Promise<false | void>;
    pageConfigs: PageConfig[];
    setPageConfigs: (menuConfigs: PageConfig[]) => void;
    reloadPageConfigs: () => Promise<false | void>;
};

export const GlobalContext: Context<Global> = createContext({
    minify: false,
    setMinify: () => { },
    hideText: false,
    setHideText: () => { },
    expandedItems: [],
    setExpandedItems: ([]) => { },
    userGroups: [],
    setUserGroups: ([]) => { },
    reloadUserGroups: async () => { },
    menuConfigs: [],
    setMenuConfigs: ([]) => { },
    reloadMenuConfigs: async () => { },
    pageConfigs: [],
    setPageConfigs: ([]) => { },
    reloadPageConfigs: async () => { },
} as Global);

export const GlobalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const auth = useAuth();
    const [minify, setMinify] = useState(false);
    const [hideText, setHideText] = useState(minify);
    const storage = useStorage();
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const [userGroups, setUserGroups] = useState<UserGroup[]>(() => storage.getUserGroups());
    const reloadUserGroups = async () => {
        const ret = await api.userGroup.get({ relations: { users: true, menuConfigs: true, pageConfigs: true } });
        if (ret.success) {
            const newUserGroup = ret.data?.sort((a, b) => a.id!.localeCompare(b.id!)).sort((a, b) => b.authority! - a.authority!) || [];
            setUserGroups(newUserGroup);
            storage.setUserGroups(newUserGroup);
        }
    };
    const [menuConfigs, setMenuConfigs] = useState<MenuConfig[]>(() => storage.getMenuConfigs());
    const reloadMenuConfigs = async () => {
        const ret = await api.menuConfig.get({ relations: { userGroups: true } });
        if (ret.success) {
            const newMenuConfig = [
                {
                    menuConfigId: uuid(),
                    id: 'root',
                    parentId: undefined,
                    descr: '根節點',
                    navigateTo: undefined,
                    iconValue: EnumIcon.fa0,
                    enable: 'N',
                    createBy: 'system',
                    createDate: new Date(),
                    updateBy: 'system',
                    updateDate: new Date(),
                    userGroups: [],
                } as MenuConfig,
                ...(ret.data?.sort((a, b) => a.parentId!.localeCompare(b.parentId!)).sort((a, b) => a.id!.localeCompare(b.id!)) || []),
            ];
            setMenuConfigs(newMenuConfig);
            storage.setMenuConfigs(newMenuConfig);
        }
    };
    const [pageConfigs, setPageConfigs] = useState<PageConfig[]>(() => storage.getPageConfigs());
    const reloadPageConfigs = async () => {
        const ret = await api.pageConfig.get({ relations: { userGroups: true } });
        if (ret.success) {
            const newPageConfigs = ret.data?.sort((a, b) => a.componentName!.localeCompare(b.componentName!)) || [];
            setPageConfigs(newPageConfigs);
            storage.setPageConfigs(newPageConfigs);
        }
    };
    useEffect(() => {
        auth.isAuth && reloadUserGroups();
        auth.isAuth && reloadMenuConfigs();
        auth.isAuth && reloadPageConfigs();
    }, [auth.isAuth]);

    return (
        <GlobalContext.Provider
            value={{
                minify,
                setMinify,
                hideText,
                setHideText,
                expandedItems,
                setExpandedItems,
                userGroups,
                setUserGroups,
                reloadUserGroups,
                menuConfigs,
                setMenuConfigs,
                reloadMenuConfigs,
                pageConfigs,
                setPageConfigs,
                reloadPageConfigs,
            }}
        >
            {children}
        </GlobalContext.Provider>
    );
};

export const useGlobal = (): Global => useContext(GlobalContext);
